import { useEventStore } from "@/event";
import type { EventCategory } from "@/types/ui";
import { storeToRefs } from "pinia";

const getEventContext = (): EventCategory | null => {
	const eventStore = useEventStore();
	const { attributes: eventAttributes } = storeToRefs(eventStore);
	return eventAttributes.value.type;
};

export const getEventResourceHeadings = (): Record<string, string> => {
	const type = getEventContext();

	return {
		event_conversion_offers_rate_5: "event.conversion_rate",
		event_conversion_offers_rate_10: "event.conversion_rate",
		event_conversion_offers_rate_15: "event.conversion_rate",
		event_conversion_offers_rate_20: "event.conversion_rate",
		event_conversion_offers_rate_30: "event.conversion_rate",
		ea_eventium_income: "event.easter_event.eventium_production",
		ea_eventium_income2: "event.easter_event.eventium_production",
		ea_eventium_income3: "event.easter_event.eventium_production",
		ea_eventium_income4: "event.easter_event.eventium_production",
		ea_eventium_income5: "event.easter_event.eventium_production",
		ea_eastertide_special1: "event.easter_event.easter_egg_reward",
		ea_eastertide_special2: "event.easter_event.more_golden_eggs",
		ea_eastertide_special4: "event.easter_event.golden_egg_reward",
		eventium: `event.${type}_event.eventium`,
		fi_eventium_income: "event.fishing_event.eventium_production",
		fi_eventium_income2: "event.fishing_event.eventium_production",
		ff_special_01: "event.firefest_event.eventium_production",
		ff_special_02: "event.firefest_event.eventium_production",
		ff_special_03: "event.firefest_event.eventium_production",
		ff_special_04: "event.firefest_event.eventium_production",
		ff_bundle_1: "event.firefest_event.eventium_production",
		ff_bundle_2: "event.firefest_event.eventium_production",
		ff_bundle_3: "event.firefest_event.eventium_production",
		ff_bundle_4: "event.firefest_event.eventium_production",
		sf_special_01: "event.solarfair_event.eventium_production",
		sf_special_02: "event.solarfair_event.eventium_production",
		sf_special_03: "event.solarfair_event.eventium_production",
		sf_bundle_1: "event.solarfair_event.eventium_production",
		sf_bundle_2: "event.solarfair_event.eventium_production",
		sf_bundle_3: "event.solarfair_event.eventium_production",
		chestKey: "event.legendary_chest_key",
		hu_bow_1: "event.hunting_event.eventium_production",
		hu_bow_3: "event.hunting_event.eventium_production",
		hu_horse_2: "event.hunting_event.eventium_production",
		hu_horse_4: "event.hunting_event.eventium_production",
		ha_special1: "event.halloween_event.pumpkin_reward",
		ha_special2: "event.halloween_event.more_jack_o_lantern",
		ha_special4: "event.halloween_event.jack_o_lantern_reward",
		ha_fire1: "event.halloween_event.eventium_production",
		ha_fire2: "event.halloween_event.eventium_production",
		ha_fire3: "event.halloween_event.eventium_production",
		ha_fire4: "event.halloween_event.eventium_production",
		pi_pack1: "event.pirates_event.pumpkin_reward",
		pi_pack2: "event.pirates_event.more_pirate_treasures",
		pi_pack4: "event.pirates_event.pirate_treasure_reward",
		pi_bundle1: "event.pirates_event.eventium_production",
		pi_bundle2: "event.pirates_event.eventium_production",
		pi_bundle3: "event.pirates_event.eventium_production",
		pi_bundle4: "event.pirates_event.eventium_production",
	};
};

export const getEventResourceDescriptions = (): Record<string, string> => {
	const type = getEventContext();

	return {
		event_conversion_offers_rate_5: "event.conversion_rate_description",
		event_conversion_offers_rate_10: "event.conversion_rate_description",
		event_conversion_offers_rate_15: "event.conversion_rate_description",
		event_conversion_offers_rate_20: "event.conversion_rate_description",
		event_conversion_offers_rate_30: "event.conversion_rate_description",
		ea_eventium_income: "upgrade.easter_egg_production_description",
		ea_eventium_income2: "upgrade.easter_egg_production_description",
		ea_eventium_income3: "upgrade.easter_egg_production_description",
		ea_eventium_income4: "upgrade.easter_egg_production_description",
		ea_eventium_income5: "upgrade.easter_egg_production_description",
		ea_eastertide_special1: "upgrade.easter_egg_reward_description",
		ea_eastertide_special2: "upgrade.golden_egg_drop_rate_description",
		ea_eastertide_special4: "upgrade.golden_egg_reward_description",
		eventium: `event.${type}_event.eventium_description`,
		fi_eventium_income: "event.fishing_event.eventium_production_boost",
		fi_eventium_income2: "event.fishing_event.eventium_production_boost",
		ff_special_01:
			"upgrade.building_production_town_level_bonus_description",
		ff_special_02: "upgrade.tax_collecting_embers_description",
		ff_special_03:
			"upgrade.building_production_town_level_bonus_description",
		ff_special_04: "event.firefest_event.eventium_production_boost",
		ff_bundle_1: "event.firefest_event.eventium_production_boost",
		ff_bundle_2: "event.firefest_event.eventium_production_boost",
		ff_bundle_3: "event.firefest_event.eventium_production_boost",
		ff_bundle_4: "event.firefest_event.eventium_production_boost",
		sf_special_01: "upgrade.building_production_flat_bonus_description",
		sf_special_02: "upgrade.increase_building_limit_description",
		sf_special_03: "upgrade.building_production_flat_bonus_description",
		sf_bundle_1: "event.solarfair_event.eventium_production_boost",
		sf_bundle_2: "event.solarfair_event.eventium_production_boost",
		sf_bundle_3: "event.solarfair_event.eventium_production_boost",
		chestKey: "event.legendary_chest_key_description",
		hu_bow_1: "event.eventium_production_boost",
		hu_bow_3: "event.eventium_production_boost",
		hu_horse_2: "event.eventium_production_boost",
		hu_horse_4: "event.eventium_production_boost",
		ha_special1: "upgrade.pumpkin_reward_description",
		ha_special2: "upgrade.jack_o_lantern_drop_rate_description",
		ha_special4: "upgrade.jack_o_lantern_reward_description",
		ha_fire1: "event.eventium_production_boost_flat",
		ha_fire2: "event.eventium_production_boost_flat",
		ha_fire3: "event.eventium_production_boost_flat",
		ha_fire4: "event.eventium_production_boost_flat",
		pi_pack1: "upgrade.pirates_map_reward_description",
		pi_pack2: "upgrade.pirates_treasure_drop_rate_description",
		pi_pack4: "upgrade.pirates_treasure_reward_description",
		pi_bundle1: "event.eventium_production_boost_flat",
		pi_bundle2: "event.eventium_production_boost_flat",
		pi_bundle3: "event.eventium_production_boost_flat",
		pi_bundle4: "event.eventium_production_boost_flat",
	};
};
